import { useMemo } from 'react';
import { getDueDateStatus } from '../utils/taskDateUtils';

/**
 * Hook to organize task records into sections
 * @param {Array} records - Array of record objects
 * @returns {Object} - Object with categorized tasks
 */
export const useTaskSections = (records) => {
  return useMemo(() => {
    const sections = {
      overdue: [],
      today: [],
      upcoming: [],
      noDate: [],
      completed: [],
    };

    records.forEach((record) => {
      if (record.attributes?.isComplete) {
        sections.completed.push(record);
      } else if (record.attributes?.dueDate) {
        const status = getDueDateStatus(record.attributes.dueDate, {
          timeZone: record.attributes?.dueDateSemantics?.timezone,
        });
        
        switch (status) {
          case 'overdue':
            sections.overdue.push(record);
            break;
          case 'today':
            sections.today.push(record);
            break;
          case 'upcoming':
            sections.upcoming.push(record);
            break;
          default:
            sections.noDate.push(record);
        }
      } else {
        sections.noDate.push(record);
      }
    });

    // Sort within sections
    const sortByDueDate = (a, b) => {
      const dateA = convertToDate(a.attributes?.dueDate);
      const dateB = convertToDate(b.attributes?.dueDate);
      if (!dateA || !dateB) return 0;
      return dateA - dateB;
    };

    const sortByCompletedAt = (a, b) => {
      const dateA = convertToDate(a.attributes?.completedAt || a.metadata?.updatedAt);
      const dateB = convertToDate(b.attributes?.completedAt || b.metadata?.updatedAt);
      if (!dateA || !dateB) return 0;
      return dateB - dateA; // Most recent first
    };

    const sortByPriorityAndDate = (a, b) => {
      // First sort by priority
      const priorityOrder = { high: 0, medium: 1, low: 2, undefined: 3 };
      const priorityA = priorityOrder[a.attributes?.priority] ?? 3;
      const priorityB = priorityOrder[b.attributes?.priority] ?? 3;
      
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      
      // Then by due date
      return sortByDueDate(a, b);
    };

    sections.overdue.sort(sortByPriorityAndDate);
    sections.today.sort(sortByPriorityAndDate);
    sections.upcoming.sort(sortByDueDate);
    sections.completed.sort(sortByCompletedAt);
    
    // No date section sorted by priority, then updated time
    sections.noDate.sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2, undefined: 3 };
      const priorityA = priorityOrder[a.attributes?.priority] ?? 3;
      const priorityB = priorityOrder[b.attributes?.priority] ?? 3;
      
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      
      // Then by updated time (most recent first)
      const dateA = convertToDate(a.metadata?.updatedAt);
      const dateB = convertToDate(b.metadata?.updatedAt);
      if (!dateA || !dateB) return 0;
      return dateB - dateA;
    });

    return sections;
  }, [records]);
};

/**
 * Convert various date formats to JavaScript Date
 * @param {Date|Firebase.Timestamp|string} date - The date to convert
 * @returns {Date} - JavaScript Date object
 */
function convertToDate(date) {
  if (!date) return null;

  // If it's already a Date
  if (date instanceof Date) {
    return date;
  }

  // If it's a Firebase Timestamp
  if (date.toDate && typeof date.toDate === 'function') {
    return date.toDate();
  }

  // If it's a string, try to parse it
  if (typeof date === 'string') {
    return new Date(date);
  }

  // If it has seconds (Firestore timestamp-like object)
  if (date.seconds) {
    return new Date(date.seconds * 1000);
  }

  return new Date(date);
}
